import { loadLayout, highlightCurrentMenu, updateBreadcrumb } from './layout';
import { PUBLIC_ROUTES } from '../constants/constantsData';
import { initAuthGuard } from '../core/auth-guard';
import { initSignalREvents } from '../core/signalr-events';
import { getCurrentRole, getUserLogin } from '../utils/common';

// TODO: nếu đã có type Role dùng chung ở đâu đó (ví dụ utils/common.ts), import lại thay vì khai báo ở đây
export type Role = 'admin' | 'branch admin' | 'user'; 

interface Route {
  // ĐÃ SỬA: html giờ là hàm trả về Promise (dynamic import) giống script,
  // thay vì import ?raw tĩnh ở đầu file. Vite sẽ tách riêng từng file HTML
  // thành chunk nhỏ, chỉ tải khi vào đúng route đó.
  html: () => Promise<{ default: string }>;
  script: () => Promise<any>;
  roles?: Role[]; // Không khai báo -> route public, không cần check quyền
}
interface MenuItem {
  id: string;
  path: string;
  idCha: string | null;
  urlFilePage: string | null;
}

// 1. Glob toàn bộ file — KHÔNG dùng { import: 'default' } nữa,
//    để loader trả về nguyên module { default: string } khớp với type Route.html
const htmlModules = import.meta.glob('../pages/**/*.html', {
  query: '?raw',
}) as Record<string, () => Promise<{ default: string }>>;

const scriptModules = import.meta.glob('../pages/**/*.ts') as Record<
  string,
  () => Promise<any>
>;

// 2. Route tĩnh: public + dashboard (không có trong menu JSON)
const staticRoutes: Record<string, Route> = {
  '/view-invoice': { html: () => import('../pages/report/view-invoice.html?raw'), script: () => import('../pages/report/view-invoice') },
  '/verify-otp': { html: () => import('../pages/auth/verify-otp.html?raw'), script: () => import('../pages/auth/verify-otp') },
  '/forgot-password': { html: () => import('../pages/auth/forgot-password.html?raw'), script: () => import('../pages/auth/forgot-password') },
  '/register': { html: () => import('../pages/auth/register.html?raw'), script: () => import('../pages/auth/register') },
  '/login': { html: () => import('../pages/auth/login.html?raw'), script: () => import('../pages/auth/login') },
  '/reset-password': { html: () => import('../pages/auth/reset-password.html?raw'), script: () => import('../pages/auth/reset-password') },
  '/403': { html: () => import('../pages/error/page403.html?raw'), script: () => import('../pages/error/page403') },
  //'/': { html: () => import('../pages/dashboard/dashboard.html?raw'), script: () => import('../pages/dashboard/dashboard'), roles: ['admin', 'branch admin'] },
  //'/dashboard': { html: () => import('../pages/dashboard/dashboard.html?raw'), script: () => import('../pages/dashboard/dashboard'), roles: ['admin', 'branch admin'] }
};

// 3. Build route động từ menus trong localStorage
function buildDynamicRoutes(): Record<string, Route> {
  const user = JSON.parse(localStorage.getItem('user') ?? '{}');
  const menus: MenuItem[] = user?.menus ?? [];
  const dynamicRoutes: Record<string, Route> = {};

  menus.forEach((menu) => {
    if (!menu.urlFilePage || !menu.path || menu.path === '#') return;

    const htmlKey = `..${menu.urlFilePage}.html`;
    const scriptKey = `..${menu.urlFilePage}.ts`;

    const htmlLoader = htmlModules[htmlKey];
    const scriptLoader = scriptModules[scriptKey];

    if (!htmlLoader || !scriptLoader) {
      console.warn(`[routes] Không tìm thấy file cho path "${menu.path}" (${htmlKey})`);
      return;
    }

    dynamicRoutes[menu.path] = {
      html: htmlLoader,
      script: scriptLoader,
    };
  });

  return dynamicRoutes;
}

export function getRoutes(): Record<string, Route> {
  var menuCase: Record<string, Route> = {};
  const currentRole = getCurrentRole();
  if(currentRole == 'user'){
      menuCase = {
        '/': { html: () => import('../pages/user/dashboard.html?raw'), script: () => import('../pages/user/dashboard'), roles: ['user'] },
        '/dashboard': { html: () => import('../pages/user/dashboard.html?raw'), script: () => import('../pages/user/dashboard'), roles: ['user'] }
      }
  }else{
      if (currentRole == 'admin') {
        menuCase = {
          '/': { html: () => import('../pages/dashboard/dashboard-admin.html?raw'), script: () => import('../pages/dashboard/dashboard-admin'), roles: ['admin'] },
          '/dashboard': { html: () => import('../pages/dashboard/dashboard-admin.html?raw'), script: () => import('../pages/dashboard/dashboard-admin'), roles: ['admin'] }
        }
      } else {
        menuCase = {
          '/': { html: () => import('../pages/dashboard/dashboard.html?raw'), script: () => import('../pages/dashboard/dashboard'), roles: ['branch admin'] },
          '/dashboard': { html: () => import('../pages/dashboard/dashboard.html?raw'), script: () => import('../pages/dashboard/dashboard'), roles: ['branch admin'] }
        }
      }
      
  }
  return {
    ...menuCase,
    ...staticRoutes,
    ...buildDynamicRoutes(),
  };
}
const routes:Record<string, Route> = getRoutes();

// Biến lưu đường dẫn đầy đủ (bao gồm cả query params nếu có) hiện tại
let currentLoadedFullPath = '';
let isNavigating = false;



export async function navigate(fullPath: string, force = false) {
  // 1. Chặn nếu đang trong quá trình chuyển trang hoặc full path không thay đổi
  if (isNavigating) return;
  if (!force && currentLoadedFullPath === fullPath) return;

  isNavigating = true;

  // 3. Tách path thuần túy (bỏ qua phần query params từ dấu '?') để tra cứu trong routes
  const cleanPath = fullPath.split('?')[0];
  const isPublicPath = PUBLIC_ROUTES.some((route) => route === cleanPath);
  try {
    const route = routes[cleanPath] || routes['/'];

    if (!isPublicPath) {
        if (!await initAuthGuard()) {
            return;
        }
        try {
            await initSignalREvents();
        } catch (error) {
            console.error('[SignalR] Init failed:', error);
        }
    }

    
    currentLoadedFullPath = fullPath;

    // 2. Đồng bộ URL hash ngay lập tức trước khi thực hiện render
    const targetHash = `#${fullPath}`;
    if (window.location.hash !== targetHash) {
      window.location.hash = targetHash;
    }

    // 4. Load Layout quản trị (bật/tắt Sidebar, Header)
    await loadLayout(cleanPath);

    // 5. Nạp HTML (dynamic import) và gắn vào thẻ #content
    // ĐÃ SỬA: route.html giờ là hàm async, phải gọi và lấy .default
    const htmlModule = await route.html();
    const contentEl = document.querySelector('#content');
    if (contentEl) {
      contentEl.innerHTML = htmlModule.default;
    }

    // 6. Nạp module script và gọi duy nhất 1 lần hàm init()
    const module = await route.script();
    if (module && typeof module.init === 'function') {
      module.init();
    }

    // 7. Cập nhật trạng thái Active Menu & Breadcrumb (chỉ áp dụng cho trang có layout admin, không phải trang public)
    if (!isPublicPath) {
      highlightCurrentMenu(cleanPath);
      updateBreadcrumb(cleanPath);
    }
  } catch (error) {
    debugger;
    console.error('Lỗi khi chuyển trang:', error);
  } finally {
    // Giải phóng cờ khóa sau khi hoàn tất
    isNavigating = false;
  }
}

// Bắt sự kiện click menu mượt mà (Hỗ trợ cả href="/..." và href="#/...")
function initRouterEvents() {
  document.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('a');
    if (!target) return;

    let href = target.getAttribute('href');

    // Bỏ qua link rỗng, dấu # đơn lẻ hoặc link javascript
    if (!href || href === '#' || href.startsWith('javascript:')) {
      return;
    }

    // Chuẩn hóa đường dẫn dạng #/path thành /path
    if (href.startsWith('#')) {
      href = href.substring(1);
    }

    // Xử lý điều hướng nếu là đường dẫn nội bộ hợp lệ
    if (href.startsWith('/')) {
      e.preventDefault();
      navigate(href);
    }
  });
}
export function startRouter() {
  // Lắng nghe sự kiện hashchange khi bấm nút Back/Forward trên trình duyệt hoặc thay đổi URL trực tiếp
  window.addEventListener('hashchange', () => {
    console.log('hashchange')
    let currentFullPath = window.location.hash.replace(/^#/, '') || '/';
    if (!currentFullPath.startsWith('/')) {
      currentFullPath = '/' + currentFullPath;
    }

    // Chỉ chuyển trang nếu full path thực sự khác với full path hiện tại
    if (currentFullPath !== currentLoadedFullPath) {
      navigate(currentFullPath);
    }
  });

  initRouterEvents();

  // Khởi chạy Route khi mới vào ứng dụng
  let initialFullPath = window.location.hash.replace(/^#/, '') || '/';
  if (!initialFullPath.startsWith('/')) {
    initialFullPath = '/' + initialFullPath;
  }
  navigate(initialFullPath, true);
}